import React from 'react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-indigo-700 to-purple-700 px-6 py-8 sm:px-10 sm:py-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Privacy Policy
            </h1>
            <p className="text-indigo-100 mt-2 text-lg">
              Vishwaraja Enterprises
            </p>
            <p className="text-indigo-200 text-sm mt-4">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          
          <div className="px-6 py-8 sm:px-10 sm:py-8">
            <div className="prose prose-indigo max-w-none">
              <p className="text-gray-600 text-lg leading-relaxed">
                At Vishwaraja Enterprises, we are committed to protecting the privacy and personal information 
                of our customers, clients, and website visitors. This Privacy Policy outlines how we collect, 
                use, and safeguard your information.
              </p>
            </div>
          </div>
        </div>

        {/* Policy Sections */}
        <div className="space-y-6">
          {/* Section 1 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-indigo-600 bg-indigo-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">1. Information We Collect</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                We may collect personal information such as your name, contact details, email address, and 
                any other information you provide while interacting with us, including through our website 
                or services.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Name</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Email Address</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Phone Number</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Company Details</span>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-indigo-600 bg-indigo-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">2. Use of Information</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed mb-3">The information we collect is used to:</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-indigo-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Provide and improve our products and services
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-indigo-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Respond to inquiries and customer support requests
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-indigo-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Process transactions and maintain records
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-indigo-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Communicate important updates and information
                </li>
              </ul>
            </div>
          </div>

          {/* Section 3 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-green-600 bg-green-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">3. Data Protection</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                We implement appropriate security measures to protect your personal information from 
                unauthorized access, misuse, or disclosure. These include encryption, secure servers, 
                and regular security assessments.
              </p>
              <div className="mt-4 flex items-center text-sm text-green-700 bg-green-50 p-3 rounded-lg">
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Your data is protected with industry-standard security protocols
              </div>
            </div>
          </div>

          {/* Section 4 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-blue-600 bg-blue-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">4. Sharing of Information</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. Information may 
                only be shared when required by law or to provide services with trusted partners under 
                confidentiality agreements.
              </p>
            </div>
          </div>

          {/* Section 5 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-yellow-600 bg-yellow-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">5. Cookies and Tracking</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                Our website may use cookies or similar technologies to enhance user experience and analyze 
                website performance. You can control cookie preferences through your browser settings.
              </p>
              <button className="mt-4 text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center">
                Manage Cookie Preferences
                <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Section 6 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-purple-600 bg-purple-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">6. Your Rights</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed mb-3">
                You have the right to access, update, or request deletion of your personal information. 
                You may contact us for any privacy-related concerns.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                <div className="border rounded-lg p-3 text-center hover:bg-gray-50 transition">
                  <div className="font-semibold text-gray-800">Access</div>
                  <div className="text-xs text-gray-500">View your data</div>
                </div>
                <div className="border rounded-lg p-3 text-center hover:bg-gray-50 transition">
                  <div className="font-semibold text-gray-800">Rectification</div>
                  <div className="text-xs text-gray-500">Correct inaccuracies</div>
                </div>
                <div className="border rounded-lg p-3 text-center hover:bg-gray-50 transition">
                  <div className="font-semibold text-gray-800">Erasure</div>
                  <div className="text-xs text-gray-500">Request deletion</div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 7 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-red-600 bg-red-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">7. Changes to This Policy</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                Vishwaraja Enterprises reserves the right to update this Privacy Policy at any time. 
                Changes will be reflected on this page. We encourage you to review this policy periodically.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section - Additional */}
        <div className="mt-8 bg-indigo-50 rounded-xl p-6 border border-indigo-100">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-800">Privacy Related Concerns?</h3>
              <p className="text-gray-600 mt-1">
                If you have any questions about this Privacy Policy or wish to exercise your rights, 
                please contact our Data Protection Officer.
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-3">
                <a href="mailto:privacy@vishwaraja.com" className="inline-flex items-center text-indigo-700 hover:text-indigo-900">
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  privacy@vishwaraja.com
                </a>
                <a href="tel:+919876543210" className="inline-flex items-center text-indigo-700 hover:text-indigo-900">
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  +91 98765 43210
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-gray-500 border-t border-gray-200 pt-6">
          <p>© {new Date().getFullYear()} Vishwaraja Enterprises. All rights reserved.</p>
          <p className="mt-1">Committed to transparency and data protection.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;